'use client';

/*
 * Client renderer for the ai-stack 'feature-product' archetype.
 * Layout aligned with /industries/* pages: IndustryHero, PageSection,
 * SectionTitle, shared typography tokens, and BookDemoButton.
 */
import { useTranslation } from '@/hooks/useTranslation';
import IndustryHero from '@/components/industry/IndustryHero';
import ProductHeroVisual from '@/components/ProductHeroVisual';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, PageSection, SectionTitle } from '@/components/ui';

const pad2 = (n) => String(n).padStart(2, '0');

/** Flatten HTML snippets to plain text for text-only slots (hero lead, SectionTitle). */
function stripParas(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/?p[^>]*>/gi, ' ')
    .replace(/<\/?(em|strong|b|i|span|ul|ol|li|a)[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

function AccentTitle({ text, accent }) {
  if (!text || !accent) return text;
  const i = text.toLowerCase().indexOf(accent.toLowerCase());
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <span className="serif-accent italic">{text.slice(i, i + accent.length)}</span>
      {text.slice(i + accent.length)}
    </>
  );
}

export default function FeatureProductClient({ slug, title, subtitle, parsed, media = {}, journey }) {
  const { t } = useTranslation();
  const { intro, sections } = parsed;

  const taglineSec = sections[0];
  const pillarSec  = sections.find((s) => s.items.length >= 3);
  const teamsIntro = sections.find((s) => /^for .* (turn|teams)/i.test(s.heading));
  const teamItems  = sections.filter((s) => /^for\s/i.test(s.heading) && s !== teamsIntro);
  const ctaSec     = sections[sections.length - 1];

  const tagline = taglineSec?.heading;
  const heroSubRaw = taglineSec?.intro || intro;
  const heroHeadline  = media.heroHeadline  || tagline;
  const heroParagraph = media.heroParagraph
    ? t(media.heroParagraph)
    : ((heroSubRaw ? stripParas(t(heroSubRaw)) : '') || subtitle);

  return (
    <div className="ai-stack-page">
      <IndustryHero
        layout="visual"
        gridStyle="grid"
        breadcrumb={{
          ariaLabel: t('Breadcrumb'),
          label: t('AI Stack'),
          href: '/ai-stack',
          current: title,
        }}
        title={<AccentTitle text={t(heroHeadline)} accent={media.accentWord} />}
        lead={heroParagraph || null}
        actions={<BookDemoButton label={t('Book a Demo')} />}
        visual={(
          <div className="ai-stack-hero-visual">
            {media.heroImage
              ? <img className="ai-stack-hero-visual__img" src={media.heroImage} alt="" draggable={false} />
              : <ProductHeroVisual slug={slug} />}
          </div>
        )}
      />

      {pillarSec && (
        <PageSection bg="page">
          <SectionTitle
            eyebrow={t('AI at the Core™')}
            title={t(pillarSec.heading)}
            subtitle={pillarSec.intro ? stripParas(t(pillarSec.intro)) : undefined}
            align="center"
            maxSub="lg"
          />
          {pillarSec.items?.length > 0 && (
            <div className="lb-product-grid">
              {pillarSec.items.map((card, j) => {
                const cardImg = media.cards?.[j];
                return (
                  <article key={j} className={`lb-product-card${cardImg ? ' lb-product-card--media' : ''}`}>
                    {!cardImg && <div className="lb-product-card__icon">{pad2(j + 1)}</div>}
                    <h3 className="lb-product-card__heading">{t(card.heading)}</h3>
                    <div className="lb-product-card__body" dangerouslySetInnerHTML={{ __html: t(card.html) }} />
                    {cardImg && (
                      <div className="lb-product-card__media">
                        <img src={cardImg} alt="" draggable={false} loading="lazy" decoding="async" />
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </PageSection>
      )}

      {(teamsIntro || teamItems.length > 0) && (
        <PageSection bg="tint">
          {teamsIntro && (
            <SectionTitle
              title={t(teamsIntro.heading)}
              subtitle={teamsIntro.intro ? stripParas(t(teamsIntro.intro)) : undefined}
              align="center"
              maxSub="md"
            />
          )}
          {teamItems.length > 0 && (
            <div className="ai-stack-teams-grid">
              {teamItems.map((tm, j) => (
                <div key={j} className="lb-product-team-item">
                  <span className="lb-product-team-item__dot" />
                  <div>
                    <div className="lb-product-team-item__heading">{t(tm.heading)}</div>
                    <div className="lb-product-team-item__body">{stripParas(t(tm.intro || ''))}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </PageSection>
      )}

      {journey && (
        <section className="lb-journey">
          <div className="lb-journey__inner">
            <div className="lb-journey__head">
              <span className="lb-eyebrow">{t('The AI-at-the-core flow')}</span>
              <h2 className="type-section-title">{t(journey.heading)}</h2>
              {journey.intro && <p className="type-body">{t(journey.intro)}</p>}
            </div>
            <div className="lb-journey__track">
              {journey.steps.map((step, j) => (
                <div key={j} className={`lb-journey__step ${j % 2 === 0 ? 'lb-left' : 'lb-right'}`}>
                  <div className="lb-journey__card">
                    <span className="lb-journey__num">{pad2(j + 1)}</span>
                    <h3 className="lb-journey__card-heading">{t(step.heading)}</h3>
                    <div className="lb-journey__card-body">{t(step.body)}</div>
                  </div>
                  <div className="lb-journey__stage">{t(step.stage)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {ctaSec && ctaSec !== pillarSec && ctaSec !== teamsIntro && !/^for\s/i.test(ctaSec.heading) && (
        <PageSection bg="page">
          <SectionTitle
            title={t(ctaSec.heading)}
            subtitle={ctaSec.intro ? stripParas(t(ctaSec.intro)) : undefined}
            align="center"
            maxSub="md"
          />
        </PageSection>
      )}

      <div id="book-demo"><BookDemoSection /></div>
    </div>
  );
}
